"use client";

/**
 * Passenger-app i18n (ADR 0289 D7) — the same shape as `apps/driver/src/i18n`:
 * a typed dictionary plus a React context, no library.
 *
 * Thai is the default and the source of truth for keys; `en.ts` is typed against it
 * so a missing or extra key is a compile error rather than a blank on someone's
 * screen. Interpolation is done by composition, not a placeholder engine, so every
 * value stays a whole sentence a translator can read.
 *
 * The preference lives in `localStorage.__pax_lang` (the driver app uses `__dp_lang`
 * — separate keys because the two apps can be installed on the same phone).
 *
 * Hydration: the server renders in Thai, so the first client render must be Thai too
 * or React reports a mismatch. The stored locale is applied in an effect, which
 * means an English user sees one frame of Thai on a cold load. Rendering nothing
 * until the effect runs would trade that for a blank flash on every load, including
 * for the majority who are on Thai.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { th, type MessageKey } from "./messages/th";
import { en } from "./messages/en";

export type Locale = "th" | "en";
export type { MessageKey };

export const LOCALES: Locale[] = ["th", "en"];
const DEFAULT_LOCALE: Locale = "th";
const LANG_KEY = "__pax_lang";
const DICTS: Record<Locale, Record<MessageKey, string>> = { th, en };

/** Read the stored locale. Safe on the server, where there is no localStorage. */
export function getStoredLocale(): Locale {
  if (typeof window === "undefined") return DEFAULT_LOCALE;
  try {
    const v = window.localStorage.getItem(LANG_KEY);
    return v === "en" || v === "th" ? v : DEFAULT_LOCALE;
  } catch {
    // Private mode and blocked site-data both throw here; a missing preference is
    // not a reason to fail to render.
    return DEFAULT_LOCALE;
  }
}

interface LocaleContextValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: MessageKey) => string;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(DEFAULT_LOCALE);

  useEffect(() => {
    const stored = getStoredLocale();
    document.documentElement.lang = stored;
    if (stored !== DEFAULT_LOCALE) setLocaleState(stored);
  }, []);

  const setLocale = useCallback((l: Locale) => {
    try {
      window.localStorage.setItem(LANG_KEY, l);
    } catch {
      // Unstorable preference still applies for this visit.
    }
    document.documentElement.lang = l;
    setLocaleState(l);
  }, []);

  const t = useCallback(
    // Falling back to Thai rather than the key: a rider seeing "trips.plate" learns
    // nothing, and a missing English string is a bug for us, not for them.
    (key: MessageKey): string => DICTS[locale][key] ?? th[key] ?? key,
    [locale],
  );

  return (
    <LocaleContext.Provider value={{ locale, setLocale, t }}>{children}</LocaleContext.Provider>
  );
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error("useLocale must be used within <LocaleProvider>");
  return ctx;
}

/** The translate function alone: `const t = useT(); t("trips.plate")`. */
export function useT(): LocaleContextValue["t"] {
  return useLocale().t;
}
